import { useRef, useState } from 'react';
import { placeOrder } from './api';
import { submitCheckout, loadPending, discardPending } from './checkout';

// The checkout state lives in App, not in the cart drawer: closing and reopening the drawer during a submission
// shows the same attempt, still busy, and only one attempt is ever in flight (MS-09).
export function useCheckout(storage, onPlaced) {
  const [pending, setPending] = useState(() => loadPending(storage));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);
  const placedRef = useRef(onPlaced);
  placedRef.current = onPlaced;

  // request: a new checkout; null: retry the pending one unchanged.
  const send = async (request) => {
    if (inFlight.current || (request && pending)) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const result = await submitCheckout({ storage, place: placeOrder, request, pending: request ? null : pending, onAttempt: setPending });
    inFlight.current = false;
    setBusy(false);
    if (result.order) {
      setPending(null);
      placedRef.current(result.order);
    } else if (result.refused) {
      setPending(null);
      const err = result.refused;
      const shortages = err.details && err.details.shortages;
      setError(shortages ? `${err.message}: ${shortages.map((s) => `${s.name} (only ${s.available} left)`).join(', ')}` : err.message);
    } else {
      setError(`We could not confirm your order (${result.unknown.message}). Retry it: it is never placed twice.`);
    }
  };

  const discard = () => {
    if (inFlight.current) return;
    discardPending(storage);
    setPending(null);
    setError(null);
  };

  return { pending, busy, error, send, discard, clearError: () => setError(null) };
}
